import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Raven from 'raven-js';
import { useEditHash, useMutateVizSettings, useVizSettings } from 'crm-index-visualization-core';
import { reduceActionsToTableViewConfig } from './viewConfigReducer';
import { RESET_VIEW } from '../../views/actions/viewsActionTypes';
import { clearTableConfigProcessedLogs } from './slice';

/**
 * Component that handles synchronization between Redux state and viz-core
 */
export const VizCoreTableConfigBridge = () => {
  const dispatch = useDispatch();
  const {
    tableConfigSyncLog
  } = useSelector(state => state.vizCoreBridge);
  const {
    data: legacyTableConfig,
    loading: legacyTableConfigLoading
  } = useVizSettings('TABLE');
  const [saveLegacyTableSettings, {
    loading: saveLegacyTableConfigLoading
  }] = useMutateVizSettings({
    onError(e) {
      Raven.captureException(e, {
        extra: {
          location: 'VizCoreBridge'
        }
      });
    }
  });
  const [editHash, setEditHash] = useEditHash();

  // Synchronize Redux changes to viz-core
  useEffect(() => {
    if (tableConfigSyncLog.length > 0 && legacyTableConfig && !legacyTableConfigLoading && !saveLegacyTableConfigLoading) {
      const logsToProcess = [...tableConfigSyncLog];

      // Check if we have a RESET_VIEW action
      const resetViewIndex = logsToProcess.findLastIndex(action => action.type === RESET_VIEW);
      const hasResetViewAction = resetViewIndex !== -1;

      // If there's a RESET_VIEW action, clear the edit hash first and wait for the new config data
      if (hasResetViewAction && editHash) {
        setEditHash(null);
        return; // Exit early to wait for the new config data to load
      }

      // If we had a RESET_VIEW action but editHash is now null (cleared),
      // only process actions that came after the last RESET_VIEW
      const actionsToProcess = hasResetViewAction ? logsToProcess.slice(resetViewIndex + 1) : logsToProcess;

      // If there are no actions to process after filtering, clear the processed logs and exit
      if (actionsToProcess.length === 0 && hasResetViewAction) {
        dispatch(clearTableConfigProcessedLogs(logsToProcess.length));
        return;
      }

      // Reduce the logs to a final table config state
      const updatedLegacyTableConfig = reduceActionsToTableViewConfig(legacyTableConfig, actionsToProcess);

      // Save to viz-core
      saveLegacyTableSettings(updatedLegacyTableConfig).then(() => {
        dispatch(clearTableConfigProcessedLogs(logsToProcess.length));
      }).catch(error => {
        // Logs will remain in the queue and be retried on next sync cycle
        console.error('Failed to sync table config to viz-core:', error);
      });
    }
  }, [tableConfigSyncLog, legacyTableConfig, legacyTableConfigLoading, saveLegacyTableConfigLoading, dispatch, saveLegacyTableSettings, setEditHash, editHash]);

  // Debug view - remove in production
  const __ = {
    hasCurrentTableConfig: !!legacyTableConfig,
    tableConfigSyncLogLength: tableConfigSyncLog.length,
    currentTableConfig: legacyTableConfig,
    editHash
  };
  return null;
};